import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { TextInput } from "@sinch/components";
import { MessageTemplates } from "@sinch/components/application/Messages/queries.graphql";
import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { useMessageTemplate } from "@sinch/components/application/Messages/useMessageTemplate";
import { notifyErrors } from "@sinch/components/tools/errorSnackbars";
import { useSnackbar } from "notistack";
import { isEmpty } from "ramda";
import React, { ChangeEventHandler, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { match, P } from "ts-pattern";
import { MessageFormFields, minTemplateSchema } from "./formSchema";

/**
 * Retrieves the appropriate content for a given set of notification channels and template data.
 */
const getContentByChannel = (channels: NotificationChannel[], data: MessageFormFields): string =>
  match(channels)
    .with(P.array(NotificationChannel.Sms), () => data.textMessage ?? "")
    .with(P.array(NotificationChannel.Email), () => data.message ?? "")
    .with(P.array(NotificationChannel.Push), () => data.pushMessage ?? "")
    .otherwise(() => "");

const createTemplateInput = (name: string, formData: MessageFormFields) => ({
  name,
  content: getContentByChannel(formData.channels, formData),
  subject: formData.subject || "",
});

/**
 * SaveAsTemplateButton component allows the user to save the current message as a reusable template.
 *
 * Behavior:
 * - Displays a button for opening the save template dialog.
 * - Within the dialog:
 *   - Allows the user to input a name for the template.
 *   - Provides a "Confirm" button to save the template (disabled if input is invalid or a saving operation is ongoing).
 *   - Includes error handling and user feedback mechanisms in case of failure.
 * - Closes the dialog and resets the state after the operation is complete or canceled.
 *
 * Key Side Effects:
 * - Dynamically refetches relevant data (like updated templates) upon successful creation using GraphQL queries.
 */
export const SaveAsTemplateButton = ({ ignoreSubject }: { ignoreSubject?: boolean }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getValues, trigger } = useFormContext<MessageFormFields>();
  const { create, loading } = useMessageTemplate();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const handleClose = useCallback(() => {
    setOpen(false);
    setName(""); // Reset form state on close
  }, []);

  const handleOpen = useCallback(() => {
    minTemplateSchema({ noRequiredSubject: ignoreSubject })
      .validate(getValues())
      .then(() => {
        setOpen(true);
      })
      .catch((e) => {
        trigger(e.path ?? undefined);
      });
  }, []);

  const handleNameChange = useCallback<ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>>(
    (event) => setName(event.target.value),
    []
  );

  const handleCreateTemplate = useCallback(async () => {
    if (!name.trim()) return;

    const formData = getValues();

    try {
      const { data } = await create({
        refetchQueries: [
          {
            query: MessageTemplates,
            variables: { filter: { channel: { IN: [...formData.channels] }, category: { EQ: null } } },
          },
        ],
        variables: {
          input: createTemplateInput(name.trim(), formData),
          channel: formData.channels[0],
        },
      });

      if (data?.createMessageTemplate.result) {
        enqueueSnackbar(t("successfullySaved"), { variant: "success" });
        handleClose();
      } else {
        notifyErrors(data?.createMessageTemplate);
      }
    } catch (error) {
      // Handle any unexpected errors
      console.error("Error creating template:", error);
      enqueueSnackbar(t("Error.somethingWentWrong"), { variant: "error" });
    }
  }, [name, getValues, create, handleClose, enqueueSnackbar, t]);

  const isCreateDisabled = isEmpty(name.trim()) || loading;

  return (
    <>
      <Button onClick={handleOpen} variant="outlined">
        {t("MessageTemplate.saveAsTemplate")}
      </Button>

      <Dialog aria-labelledby="save-template-dialog-title" fullWidth maxWidth="xs" onClose={handleClose} open={open}>
        <DialogTitle id="save-template-dialog-title">{t("MessageTemplate.saveAsTemplate")}</DialogTitle>

        <DialogContent>
          <TextInput
            autoFocus
            fullWidth
            label={t("name")}
            onChange={handleNameChange}
            placeholder={t("name")}
            value={name}
          />
        </DialogContent>

        <DialogActions>
          <Button disabled={loading} onClick={handleClose}>
            {t("cancel")}
          </Button>
          <Button disabled={isCreateDisabled} loading={loading} onClick={handleCreateTemplate} variant="contained">
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
